import { ResultBase } from '@intranet/core/types';
import moment from 'moment';
import { User, UserGroup, UserRoleMember } from '@intranet/modules/users/user.types';

export class BankHoliday {
    public bankHolidayId: number;
    public date: string;
    public description: string;
}

export class HolidayEntitlement {
    public holidayEntitlementId: number;
    public userId: string;
    public user: User;
    public days: number;
    public description: string;
    public dateCreated: string;
    public isExpired: boolean;
    public year: number;
}

export enum HolidayType {
    AnnualLeave = 0,
    Illness = 20,
    Other = 40
}

export class Holiday {
    public holidayId: number;
    public userId: string;
    public user: User;
    public start: string;
    public duration: number;
    public description: string;
    public dateCreated: string;
    public createdByUserId: string;
    public createdByUser: User;
    public lastDay: string;
    public entitlementUsed: number;
    public isMorning: boolean;
    public holidayType: HolidayType;
}

export class HolidayWithDates extends Holiday {
    public startDate: moment.Moment;
    public lastDayDate: moment.Moment;

    public static MakeDates(hol: Holiday): HolidayWithDates {
        let hwd = hol as HolidayWithDates;
        hwd.startDate = moment(hwd.start);
        hwd.lastDayDate = moment(hwd.lastDay);
        return hwd;
    }
}

export class GetBankHolidaysResult extends ResultBase {
    public bankHolidays: BankHoliday[];
}

export class AddBankHolidayResult extends ResultBase {
    public bankHoliday: BankHoliday;
}

export class GetBankHolidayResult extends ResultBase {
    public bankHoliday: BankHoliday;
}

export class UpdateBankHolidayResult extends ResultBase {
    public bankHoliday: BankHoliday;
}

export class DeleteBankHolidayResult extends ResultBase {}

export class AddBankHolidayYearResult extends ResultBase {}

export class AddEntitlementsResult extends ResultBase {}

export class GetEntitlementsResult extends ResultBase {
    public entitlements: HolidayEntitlement[];
    public users: User[];
}
export class AddHolidayResult extends ResultBase {
    public holiday: Holiday;
}

export class GetHolidaysResult extends ResultBase {
    public holidays: Holiday[];
    public bankHolidays: BankHoliday[];
}

export class DeleteHolidayResult extends ResultBase {}
export class DeleteEntitlementResult extends ResultBase {}

export class CalculateUsageResult extends ResultBase {
    public isOverlapWithExisting: boolean;
    public isOverlapWithUnavailable: boolean;
    public entitlementConsumed: number;
    public entitlementBefore: number;
    public entitlementAfter: number;
    public daysOfTypeUsed: number;
    public totalDaysOfTypeUsed: number;
    public bankHolidays: BankHoliday[];
    public holidaysOverlapped: Holiday[];
    public otherHolidaysOverlapped: string;
}

export class EntitlementConsumedForUser {
    public userName: string;
    public userId: string;
    public entitlementConsumed: number;
    public entitlementBefore: number;
    public entitlementAfter: number;
}


export class CalculateMultipleUsageResult extends ResultBase {
    public results: EntitlementConsumedForUser[];
}

export class HolidaysBase extends ResultBase {
    public users: User[];
    public roles: UserRoleMember[];
    public bankHolidays: BankHoliday[];
    public holidays: Holiday[];
}

export class BlockedRange {
    public start: string;
    public end: string;

    // add a public getter for the start date as a moment
    public get startDate(): moment.Moment {
        return moment(this.start);
    }
    // add a public getter for the end date as a moment
    public get endDate(): moment.Moment {
        return moment(this.end);
    }
}

export class InitAddHolidayResult extends HolidaysBase {
    public notAvailable: BlockedRange[];
}

export class GetHolidaysGridResult extends HolidaysBase {
    public entitlements: HolidayEntitlement[];
    public nonWorkingDays: NonWorkingDays[];
}

export class AddEntitlementResult extends ResultBase {
    public entitlement: HolidayEntitlement;
}

export class HolidayUsage {
    public month: Date;
    public workingDays: number;
    public holidaysBooked: number;
    public daysWorked: number;
    public adjustedDaysWorked: number;
}

export class GetHolidayUsageResult extends ResultBase {
    public year: number;
    public usage: HolidayUsage[];
}

export class NonWorkingDays {
    public nonWorkingDaysId: number;
    public userId: string;
    public user: User;
    public firstDay: string;
    public lastDay: string;
    public description: string;
    public monday: boolean;
    public tuesday: boolean;
    public wednesday: boolean;
    public thursday: boolean;
    public friday: boolean;
    public createdByUserId: string;
    public createdByUser: User;
}

export class GetNonWorkingDaysResult extends ResultBase {
    public nonWorkingDays: NonWorkingDays[];
}

export class DeleteNonWorkingDaysResult extends ResultBase {}

export interface AvailabilityResult {
    date: string;
    availableUsers: { user: User }[];
    unavailableUsers: { user: User; reason: 'Holiday' | 'Non-working day' | 'Half day' }[];
    availability: { user: User; isAvailable: boolean; reason: string }[];
}

// Mirrors the backend's AvailabilityIndicator enum (HolidayHelper.cs). Says
// when the user IS available, not which half is booked — Morning means
// available in the morning only (the afternoon is booked), and vice versa.
export enum AvailabilityIndicator {
    AllDay = 0,
    Morning = 1,
    Afternoon = 2,
    Unavailable = 3
}

// Shape of GetAllUserAvailability's per-user "user" field — the API returns
// ShortUser, a smaller projection than the full User model used elsewhere.
export interface AvailabilityUser {
    id: string;
    firstName: string;
    surname: string;
    shortName: string;
    email: string;
    username: string;
    active: boolean;
    userGroup?: UserGroup;
}

export interface UserAvailabilityDay {
    date: string;
    availability: AvailabilityIndicator;
    reason: string;
}

export interface UserWeekAvailability {
    user: AvailabilityUser;
    days: UserAvailabilityDay[];
}

export class GetAllUserAvailabilityResult extends ResultBase {
    public startDate: string;
    public users: UserWeekAvailability[];
}